import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { VariableSizeList } from 'react-window';

import { usePrevious } from 'flow/hooks';
import { useAppSelector } from 'flow/hooks/useRedux';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import { routes } from 'flow/types/routes';
import { listRowTarget } from 'flow/components/nav/list-keys/list-row-focus';

interface Options {
    isEnabled: boolean;
    containerRef: RefObject<HTMLDivElement>;
    listRef: RefObject<VariableSizeList>;
    rowToFocus: () => number;
}

// In the narrow layout a contact card hides the list, and the hidden list
// renders no rows. When the list shows again, focus goes to the row the
// user left from, once react-window has rendered it.
const useFocusListOnReturn = ({
    isEnabled,
    containerRef,
    listRef,
    rowToFocus,
}: Options) => {
    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);
    const listRouteIsShown =
        useRouteMatch({ path: routes['extensions'], exact: true }) !== null;
    const listIsShown = !isSinglePaneMode || listRouteIsShown;
    const listWasShown = usePrevious(listIsShown);

    const pendingIndexRef = useRef<number | null>(null);
    const hasScrolledRef = useRef(false);

    const focusPendingRow = useCallback(() => {
        const index = pendingIndexRef.current;
        if (index === null) {
            return;
        }

        const target = listRowTarget(containerRef.current, index);
        if (target) {
            target.focus();
            pendingIndexRef.current = null;
        } else if (listRef.current && !hasScrolledRef.current) {
            hasScrolledRef.current = true;
            listRef.current.scrollToItem(index);
        } else if (listRef.current) {
            pendingIndexRef.current = null;
        }
    }, [containerRef, listRef]);

    useEffect(() => {
        if (!isEnabled || !listIsShown || listWasShown !== false) {
            return;
        }

        const index = rowToFocus();
        if (index !== -1) {
            pendingIndexRef.current = index;
            hasScrolledRef.current = false;
            focusPendingRow();
        }
    }, [isEnabled, listIsShown, listWasShown, rowToFocus, focusPendingRow]);

    return focusPendingRow;
};

export default useFocusListOnReturn;
